import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Button,
  Paper,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AddCircleOutline } from "@material-ui/icons";
import CardapioCard from "../components/CardapioCard";
import CardapioModal from "../components/CardapioModal";
import api from "../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    position: "absolute",
    top: 64,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#edf1f5",
    overflowY: "auto",
    overflowX: "hidden",
    padding: theme.spacing(4),
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
      top: 56,
      padding: theme.spacing(2),
    },
  },
  titulo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(4),
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: theme.spacing(2.5, 3),
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      padding: theme.spacing(1.5, 2),
      gap: theme.spacing(1),
    },
  },
  tituloEsquerda: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(4),
    [theme.breakpoints.down("sm")]: {
      gap: theme.spacing(2),
      alignItems: "flex-start",
      width: "100%",
      flexWrap: "wrap",
    },
  },
  tituloTexto: {
    fontWeight: 700,
    fontSize: "1.5rem",
    color: "#111827",
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
    },
  },
  botaoNovo: {
    backgroundColor: "#16a34a",
    color: "#fff",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 10,
    padding: theme.spacing(1, 3),
    boxShadow: "0 3px 8px rgba(22,163,74,0.3)",
    "&:hover": {
      backgroundColor: "#15803d",
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      padding: theme.spacing(0.75, 2),
      fontSize: "0.95rem",
      borderRadius: 8,
      alignSelf: "center",
    },
  },
  gridContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(6),
  },
}));

export default function Cardapio() {
  const classes = useStyles();

  const dias = [
    { dia: "Segunda-feira", cor: "#f97316" },
    { dia: "Terça-feira", cor: "#fb923c" },
    { dia: "Quarta-feira", cor: "#16a34a" },
    { dia: "Quinta-feira", cor: "#2563eb" },
    { dia: "Sexta-feira", cor: "#4f46e5" },
    { dia: "Sábado", cor: "#9333ea" },
    { dia: "Domingo", cor: "#ef4444" },
    { dia: "Extra / Exceção", cor: "#6b7280" },
  ];

  const [modalAberto, setModalAberto] = useState(false);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [cardapios, setCardapios] = useState({});
  const [loading, setLoading] = useState(false);
  const [excecaoAtiva, setExcecaoAtiva] = useState(false); // 🔹 nova flag

  const abrirModal = (dia, visualizar = false) => {
    setDiaSelecionado(dia);
    setModoVisualizacao(visualizar);
    setModalAberto(true);
  };

  // 🔹 Carrega todos os cardápios
  const carregarCardapios = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cardapio");
      if (data?.success) {
        const mapa = {};
        data.data.forEach((c) => (mapa[c.dia] = c));
        setCardapios(mapa);
      }
    } catch (error) {
      console.error("❌ Erro ao buscar cardápios:", error);
      toast.error("Erro ao carregar cardápios");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Salvar ou atualizar cardápio
  const salvarCardapio = async (novoCardapio) => {
    try {
      await api.post("/cardapio", novoCardapio);
      toast.success("Cardápio salvo com sucesso!");
      await carregarCardapios();
      await carregarStatusExcecao(); // sincroniza o switch
    } catch (error) {
      console.error("❌ Erro ao salvar cardápio:", error);
      toast.error("Erro ao salvar cardápio");
    }
  };

  // 🔹 Alternar o modo exceção
  const alternarExcecao = async (ativado) => {
    try {
      setExcecaoAtiva(ativado);

      await api.post("/cardapio/excecao", { excecao: ativado ? 1 : 0 });
      toast.success(
        ativado
          ? "Cardápio Exceção ativado ✅"
          : "Cardápio Exceção desativado ❌"
      );
    } catch (error) {
      console.error("❌ Erro ao atualizar exceção:", error);
      toast.error("Erro ao atualizar Cardápio Exceção");
    }
  };

  // 🔹 Buscar o status atual do modo exceção no banco
  const carregarStatusExcecao = async () => {
    try {
      const { data } = await api.get("/cardapio/excecao");
      if (data?.success) setExcecaoAtiva(data.excecao === 1);
    } catch (err) {
      console.error("❌ Erro ao carregar status de exceção:", err);
    }
  };

  useEffect(() => {
    carregarCardapios();
    carregarStatusExcecao();
  }, []);

  return (
    <Box className={classes.container}>
      <Paper elevation={0} className={classes.titulo}>
        <Box className={classes.tituloEsquerda}>
          <Typography className={classes.tituloTexto}>
            🍽️ Cardápios da Semana
          </Typography>

          {/* 🔹 Switch de Cardápio Exceção */}
          <FormControlLabel
            control={
              <Switch
                checked={excecaoAtiva}
                onChange={(e) => alternarExcecao(e.target.checked)}
                color="primary"
              />
            }
            label="Cardápio Exceção"
          />
        </Box>

        <Button
          variant="contained"
          className={classes.botaoNovo}
          startIcon={<AddCircleOutline />}
          onClick={() => abrirModal("Extra / Exceção", false)}
        >
          Novo Cardápio
        </Button>
      </Paper>

      {loading ? (
        <Box className={classes.loadingBox}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <Grid container spacing={3} className={classes.gridContainer}>
          {dias.map((d, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <CardapioCard
                dia={d.dia}
                cor={d.cor}
                dados={cardapios[d.dia]}
                onEditar={() => abrirModal(d.dia, false)}
                onVisualizar={() => abrirModal(d.dia, true)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <CardapioModal
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        dia={diaSelecionado}
        visualizar={modoVisualizacao}
        onSalvar={salvarCardapio}
        dados={cardapios[diaSelecionado]}
      />
    </Box>
  );
}
